import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  AlertCircle,
  AlertTriangle,
  Clock,
  MapPin,
  Users,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { SectionCard } from '@/components/ui/section-card';
import { SectionLabel } from '@/components/ui/section-label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  useMyUpcomingRodeos,
  type UpcomingBuddyGroup,
  type UpcomingBuddyGroupMember,
  type UpcomingRodeoEntry,
} from '@/api/extensions/useDashboardData';
import {
  useCancelTeamRegistration,
  useOutstandingBalance,
} from '@/api/extensions/useRegistrationOps';
import {
  formatDateRange,
  formatCurrency,
  getRelativeTime,
} from '@/utils/formatters';
import { formatPreferenceLabel } from '@/utils/format';
import { formatInTimezone, getTimezoneAbbreviation } from '@/utils/timezone';
import { getEventDisplayTitle } from '@/utils/eventTypes';
import { useToast } from '@/contexts/ToastContext';
import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CancelRegistrationDialog } from '@/components/events/CancelRegistrationDialog';
import { PayNowDialog } from '@/components/events/PayNowDialog';
import { EditPreferencesDialog } from '@/components/events/EditPreferencesDialog';
import { EntryPreferencesDisplay } from '@/components/events/EntryPreferencesDisplay';
import { FindNewPartnerDialog } from '@/components/events/FindNewPartnerDialog';
import {
  detectConflictsForEvent,
  type EntryWithPreferences,
} from '@/utils/entryPreferences';

/**
 * Shape coming back from /dashboard-data/my/upcoming-rodeos. Mirrors the
 * dashboard service's getUpcomingRodeos() return — kept loose with `any`
 * for the registration rows since they go through the existing dialogs
 * which already accept the wider registration shape.
 */
type RodeoEntry = UpcomingRodeoEntry & {
  registrations: any[];
};

export function UpcomingEventsSection() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { data: rodeos, isLoading } = useMyUpcomingRodeos();
  // Outstanding-balance lookup powers the "Balance Due" badge + Pay Now
  // button on confirmed rows that have a supplemental owed (post-rejection
  // non-member surcharge OR a partner-swap supplemental).
  const { data: outstandingBalance } = useOutstandingBalance();
  const owedByRegistrationId = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of outstandingBalance?.unpaid_events ?? []) {
      if (e.registration_id) map.set(e.registration_id, e.amount_owed);
    }
    return map;
  }, [outstandingBalance]);

  const cancelRegistration = useCancelTeamRegistration();
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{
    id: string;
    description?: string;
    refundEstimate?: {
      paidTotal: number;
      entryFee: number;
      nonMemberFee?: number;
      transactionFee: number;
      partnerSwapFeeRetained?: number;
      refundable: number;
      currency?: string;
    };
  } | null>(null);
  const [payNow, setPayNow] = useState<{
    registrationId: string;
    eventName: string;
    fallbackTotal: number;
    transactionFeeTotal: number;
  } | null>(null);
  // Pay Now clicked on an entry whose current payment is a bank (ACH) debit
  // still verifying micro-deposits. Continuing supersedes that pending ACH, so
  // we interpose this warning (mirrors the event-detail page) rather than
  // silently abandoning a verification the contestant already started.
  const [verifyWarn, setVerifyWarn] = useState<{
    registrationId: string;
    eventName: string;
    fallbackTotal: number;
    transactionFeeTotal: number;
  } | null>(null);
  const [editPrefs, setEditPrefs] = useState<{
    registrationId: string;
    eventId: string;
    rodeoId: string | null;
    eventName: string;
    currentFirst: string | null;
    currentSecond: string | null;
  } | null>(null);
  const [findPartner, setFindPartner] = useState<{
    registrationId: string;
    eventId: string | null;
    primaryPosition: string | null;
    eventName: string;
  } | null>(null);
  // Collapsed-state bookkeeping. Default is expanded; users opt into
  // collapse by clicking the rodeo header — keeps the dashboard scannable
  // for users with one or two rodeos while still letting power users with
  // many rodeos compress the view. Keyed by rodeo id so toggle state
  // survives across the conditional rerenders inside this section.
  const [collapsedRodeos, setCollapsedRodeos] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleRodeo = (rodeoId: string) => {
    setCollapsedRodeos((prev) => {
      const next = new Set(prev);
      if (next.has(rodeoId)) next.delete(rodeoId);
      else next.add(rodeoId);
      return next;
    });
  };

  const handleCancelClick = (
    registrationId: string,
    description?: string,
    refundEstimate?: {
      paidTotal: number;
      entryFee: number;
      nonMemberFee?: number;
      transactionFee: number;
      partnerSwapFeeRetained?: number;
      refundable: number;
      currency?: string;
    },
  ) => {
    setPendingCancel({ id: registrationId, description, refundEstimate });
  };

  const handleCancelConfirm = async () => {
    if (!pendingCancel) return;
    const registrationId = pendingCancel.id;
    setCancellingId(registrationId);
    try {
      const result = await cancelRegistration.mutateAsync(registrationId);
      const successfulRefunds = (result?.refunds ?? []).filter(
        (r) => r.refunded,
      );
      const totalRefunded = successfulRefunds.reduce(
        (sum, r) => sum + (r.amount_refunded ?? 0),
        0,
      );
      const totalFeeRetained = successfulRefunds.reduce(
        (sum, r) => sum + (r.transaction_fee_retained ?? 0),
        0,
      );
      if (successfulRefunds.length > 0 && totalRefunded > 0) {
        const refunded = formatCurrency(totalRefunded);
        const fee = totalFeeRetained;
        const detail =
          fee > 0
            ? `Refunded ${refunded} to your original payment method. The ${formatCurrency(fee)} transaction fee is non-refundable.`
            : `Refunded ${refunded} to your original payment method.`;
        showSuccess('Entry cancelled', detail);
      } else {
        showSuccess('Entry cancelled');
      }
      setPendingCancel(null);
    } catch {
      showError('Failed to cancel entry');
    } finally {
      setCancellingId(null);
    }
  };

  // Per-rodeo conflict map. Same logic as before — preferences live on
  // each registration but are rodeo-wide in practice, so we group by
  // rodeo id and run detectConflictsForEvent over the full set.
  const conflictsByRodeoId = useMemo(() => {
    const result = new Map<string, ReturnType<typeof detectConflictsForEvent>>();
    for (const r of (rodeos ?? []) as RodeoEntry[]) {
      const rodeoId = r.rodeo?.id;
      if (!rodeoId) continue;
      // Anchor preference labels in the conflict copy to the rodeo's
      // saved zone with the abbreviation appended — falls back to
      // Pacific only when the column was never populated.
      const tz =
        (typeof r.rodeo?.timezone === 'string' && r.rodeo.timezone) ||
        'America/Los_Angeles';
      result.set(
        rodeoId,
        detectConflictsForEvent(
          r.registrations as EntryWithPreferences[],
          tz,
        ),
      );
    }
    return result;
  }, [rodeos]);

  if (isLoading) {
    return (
      <SectionCard title="Your Upcoming Rodeos">
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      </SectionCard>
    );
  }

  const list = (rodeos ?? []) as RodeoEntry[];
  const isEmpty = list.length === 0;

  return (
    <SectionCard title="Your Upcoming Rodeos">
      {isEmpty ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="rounded-full bg-muted p-4 mb-4">
            <Calendar className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            No upcoming rodeos
          </h3>
          <p className="text-sm text-muted-foreground max-w-sm mb-4">
            Browse rodeos or jackpots to find your next competition.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button variant="outline" onClick={() => navigate('/rodeos')}>
              Browse Rodeos
            </Button>
            <Button variant="outline" onClick={() => navigate('/jackpots')}>
              Browse Jackpots
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {list.map((entry) => (
            <RodeoCard
              key={entry.rodeo?.id ?? Math.random()}
              entry={entry}
              collapsed={!!entry.rodeo?.id && collapsedRodeos.has(entry.rodeo.id)}
              onToggle={toggleRodeo}
              conflict={
                entry.rodeo?.id
                  ? conflictsByRodeoId.get(entry.rodeo.id) ?? null
                  : null
              }
              owedByRegistrationId={owedByRegistrationId}
              cancellingId={cancellingId}
              onCancelClick={handleCancelClick}
              onPayNow={({ achVerificationPending, ...payload }) => {
                // Mid-ACH-verification: warn before superseding the pending
                // bank debit; otherwise open checkout directly.
                if (achVerificationPending) setVerifyWarn(payload);
                else setPayNow(payload);
              }}
              onEditPrefs={(payload) => setEditPrefs(payload)}
              onFindPartner={(payload) => setFindPartner(payload)}
              onNavigate={navigate}
            />
          ))}
        </div>
      )}

      <CancelRegistrationDialog
        open={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        onConfirm={handleCancelConfirm}
        loading={cancellingId !== null}
        description={pendingCancel?.description}
        refundEstimate={pendingCancel?.refundEstimate}
      />
      <PayNowDialog
        open={payNow !== null}
        onClose={() => setPayNow(null)}
        registrationId={payNow?.registrationId ?? ''}
        eventName={payNow?.eventName}
        fallbackTotal={payNow?.fallbackTotal ?? null}
        transactionFeeTotal={payNow?.transactionFeeTotal ?? null}
      />
      {/* ACH-verification warning: paying a different way supersedes the
          pending bank debit (the backend cancels the unverified PaymentIntent
          on completion). Make the trade-off explicit — mirrors the event-detail
          page so the dashboard doesn't silently abandon a verification. */}
      <Dialog
        open={verifyWarn !== null}
        onOpenChange={(next) => {
          if (!next) setVerifyWarn(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <DialogTitle>Bank verification still pending</DialogTitle>
            </div>
            <DialogDescription>
              {verifyWarn?.eventName
                ? `This entry (${verifyWarn.eventName}) has a bank `
                : 'This entry has a bank '}
              (ACH) payment that's still waiting for microdeposit verification.
              If you continue with a different payment method, that pending ACH
              will be cancelled so you aren't charged twice. You can also close
              this and finish verifying your bank instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setVerifyWarn(null)}>
              Keep waiting
            </Button>
            <Button
              onClick={() => {
                const next = verifyWarn;
                setVerifyWarn(null);
                if (next) setPayNow(next);
              }}
            >
              Continue with new payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <EditPreferencesDialog
        open={editPrefs !== null}
        onClose={() => setEditPrefs(null)}
        registrationId={editPrefs?.registrationId ?? ''}
        eventId={editPrefs?.eventId ?? ''}
        rodeoId={editPrefs?.rodeoId ?? null}
        currentFirst={editPrefs?.currentFirst ?? null}
        currentSecond={editPrefs?.currentSecond ?? null}
        eventName={editPrefs?.eventName}
      />
      <FindNewPartnerDialog
        open={findPartner !== null}
        onClose={() => setFindPartner(null)}
        registrationId={findPartner?.registrationId ?? ''}
        eventId={findPartner?.eventId ?? null}
        primaryPosition={findPartner?.primaryPosition ?? null}
        eventName={findPartner?.eventName}
      />
    </SectionCard>
  );
}

interface RodeoCardProps {
  entry: RodeoEntry;
  collapsed: boolean;
  onToggle: (rodeoId: string) => void;
  conflict: ReturnType<typeof detectConflictsForEvent> | null;
  owedByRegistrationId: Map<string, number>;
  cancellingId: string | null;
  onCancelClick: (
    registrationId: string,
    description?: string,
    refundEstimate?: any,
  ) => void;
  onPayNow: (payload: {
    registrationId: string;
    eventName: string;
    fallbackTotal: number;
    transactionFeeTotal: number;
    /** True when the row's current payment is a bank (ACH) debit still
     *  verifying micro-deposits — the parent interposes a supersede warning. */
    achVerificationPending?: boolean;
  }) => void;
  onEditPrefs: (payload: {
    registrationId: string;
    eventId: string;
    rodeoId: string | null;
    eventName: string;
    currentFirst: string | null;
    currentSecond: string | null;
  }) => void;
  onFindPartner: (payload: {
    registrationId: string;
    eventId: string | null;
    primaryPosition: string | null;
    eventName: string;
  }) => void;
  onNavigate: (to: string) => void;
}

function RodeoCard(props: RodeoCardProps) {
  const {
    entry,
    collapsed,
    onToggle,
    conflict,
    owedByRegistrationId,
    cancellingId,
    onCancelClick,
    onPayNow,
    onEditPrefs,
    onFindPartner,
    onNavigate,
  } = props;
  const rodeo = entry.rodeo;
  if (!rodeo?.id) return null;

  const startDate = rodeo.start_date ?? null;
  const endDate = rodeo.end_date ?? null;
  const entryClose = rodeo.entry_close_time ?? null;
  const entryCloseMs = entryClose ? new Date(entryClose).getTime() : NaN;
  const entriesClosed =
    Number.isFinite(entryCloseMs) && entryCloseMs <= Date.now();
  const entryCloseSoon =
    !entriesClosed &&
    Number.isFinite(entryCloseMs) &&
    entryCloseMs - Date.now() < 48 * 3600000;

  // Pin every wall-clock display to the rodeo's saved zone so a contestant
  // in a different timezone sees the time the host actually entered, with
  // a US-style abbreviation appended ("Fri, May 15, 6:00 PM PT"). Falls
  // back to Pacific (the column's DB default) when older payloads don't
  // surface the field. Plumbed into preference formatters + entry-close
  // helpers below.
  const rodeoTimezone =
    (typeof rodeo.timezone === 'string' && rodeo.timezone) ||
    'America/Los_Angeles';
  const tzAbbr = getTimezoneAbbreviation(rodeoTimezone);
  // Absolute "closes on…" string for the warning state — once entries are
  // about to close (<48h) the relative time alone reads ambiguously across
  // zones, so we pin the absolute time to the rodeo's tz with abbr.
  const entryCloseAbsolute = entryClose
    ? formatInTimezone(
        entryClose,
        rodeoTimezone,
        { dateStyle: 'medium', timeStyle: 'short' },
        true,
      )
    : null;

  const buddyGroups = entry.buddy_groups ?? [];
  const registrations = entry.registrations ?? [];
  const totalInvolvement = buddyGroups.length + registrations.length;

  return (
    <div className="rounded-xl border overflow-hidden">
      {/* Rodeo header — clickable to collapse/expand */}
      <button
        type="button"
        onClick={() => onToggle(rodeo.id!)}
        className="w-full flex items-start justify-between gap-3 px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
      >
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            {collapsed ? (
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            <h3 className="font-semibold text-base truncate">
              {rodeo.name || 'Unnamed rodeo'}
            </h3>
            {rodeo.rodeo_or_jackpot === 'jackpot' && (
              <Badge variant="outline" className="text-[10px] uppercase">
                Jackpot
              </Badge>
            )}
            <Badge variant="secondary" className="text-xs">
              {totalInvolvement}{' '}
              {totalInvolvement === 1 ? 'entry' : 'entries'}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {startDate && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDateRange(startDate, endDate)}
              </span>
            )}
            {rodeo.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {rodeo.location}
              </span>
            )}
            {entryClose && (
              <span
                className={
                  'flex items-center gap-1 ' +
                  (entriesClosed
                    ? 'text-muted-foreground'
                    : entryCloseSoon
                      ? 'text-amber-600 dark:text-amber-400 font-medium'
                      : '')
                }
                // Tooltip pins the absolute wall-clock close time to the
                // rodeo's saved timezone with abbreviation so a viewer in
                // a different zone never has to guess what "in 6 hours"
                // resolves to in their own clock.
                title={entryCloseAbsolute ?? undefined}
              >
                <Clock className="h-3 w-3" />
                {entriesClosed
                  ? `Entries closed${entryCloseAbsolute ? ` (${entryCloseAbsolute})` : ''}`
                  : entryCloseSoon && entryCloseAbsolute
                    ? `Entries close ${getRelativeTime(entryClose)} — ${entryCloseAbsolute}`
                    : `Entries close ${getRelativeTime(entryClose)}${tzAbbr ? ` ${tzAbbr}` : ''}`}
              </span>
            )}
          </div>
        </div>
      </button>

      {!collapsed && (
        <div className="p-4 space-y-4">
          {/* Buddy group section. Header gives the section an identity
              so it doesn't visually blend into the My Events list below
              when both are present at this rodeo. */}
          {buddyGroups.length > 0 && (
            <section className="space-y-2">
              <SectionLabel
                icon={<Users className="h-3 w-3" />}
                color="violet"
              >
                Buddy Groups
              </SectionLabel>
              <div className="space-y-2">
                {buddyGroups.map((bg) => (
                  <BuddyGroupRow
                    key={bg.id}
                    group={bg}
                    rodeoId={rodeo.id!}
                    rodeoTimezone={rodeoTimezone}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Per-event registration rows. Heavier divider above this
              section when buddy groups are also present, so the user
              instantly sees these are their own individual entries —
              not buddy-group plumbing. */}
          {registrations.length > 0 && (
            <section
              className={
                buddyGroups.length > 0
                  ? 'space-y-2 pt-3 border-t-2 border-foreground/10'
                  : 'space-y-2'
              }
            >
              <SectionLabel
                icon={<Calendar className="h-3 w-3" />}
                color="primary"
              >
                My Events
              </SectionLabel>
              <div className="space-y-2">
                {registrations.map((reg: any) => (
                  <RegistrationRow
                    key={reg.registration_id ?? reg.id}
                    reg={reg}
                    rodeoEntriesClosed={entriesClosed}
                    rodeoTimezone={rodeoTimezone}
                    cancellingId={cancellingId}
                    owedByRegistrationId={owedByRegistrationId}
                    onCancelClick={onCancelClick}
                    onPayNow={onPayNow}
                    onEditPrefs={onEditPrefs}
                    onFindPartner={onFindPartner}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            </section>
          )}

          {/* No event entries yet — buddy-group-only rodeo */}
          {registrations.length === 0 && buddyGroups.length > 0 && (
            <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              You're attached to this rodeo via your buddy group, but
              haven't entered any individual events yet.{' '}
              <button
                type="button"
                className="underline font-medium text-foreground hover:text-primary"
                onClick={() => onNavigate(`/rodeos/${rodeo.id}`)}
              >
                Browse events
              </button>
            </div>
          )}

          {/* Per-rodeo preference conflict banner */}
          {conflict?.hasConflict && (
            <div className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <div className="font-medium">
                  {conflict.kind === 'team_partner'
                    ? 'Different preferences from partner'
                    : conflict.kind === 'multi_entry'
                      ? 'Different preferences across your entries at this rodeo'
                      : 'Different preferences detected'}
                </div>
                <div className="text-amber-700/90 dark:text-amber-300/90">
                  {conflict.reason} Informational only — if it's what you
                  intended, you can ignore this.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface BuddyGroupRowProps {
  group: UpcomingBuddyGroup;
  rodeoId: string;
  /**
   * IANA timezone of the parent rodeo. Threaded down so every preference
   * datetime renders in the host's saved zone with the abbreviation
   * appended ("Fri, May 15, 6:00 PM PT") rather than drifting to the
   * viewer's locale.
   */
  rodeoTimezone: string;
  onNavigate: (to: string) => void;
}

function BuddyGroupRow({
  group,
  rodeoId,
  rodeoTimezone,
  onNavigate,
}: BuddyGroupRowProps) {
  const first = formatPreferenceLabel(group.preferences_first, rodeoTimezone);
  const second = formatPreferenceLabel(group.preferences_second, rodeoTimezone);
  const locked = !!group.preferences_locked_at;
  // Local expand state — buddy-group breakdowns default collapsed because
  // the rodeo card is already expanded; dashboard shouldn't dump every
  // member's events on the user by default. Click the row (or the chevron)
  // to drill in.
  const [expanded, setExpanded] = useState(false);
  const totalMemberEvents = group.members.reduce(
    (sum, m) => sum + m.event_count,
    0,
  );

  return (
    // Heavier violet styling on the buddy-group card (border-2,
    // saturated border, brighter bg) so it pairs well with the
    // primary-accented My Events cards below — they read as two
    // distinct sections at a glance.
    <div className="relative rounded-lg border-2 border-violet-300 bg-violet-50 dark:bg-violet-950/40 dark:border-violet-800 overflow-hidden">
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1 bg-violet-500/70"
      />
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full text-left pl-4 pr-3 py-2.5 flex items-start justify-between gap-2 hover:bg-violet-100 dark:hover:bg-violet-950/60 transition-colors"
      >
        <div className="min-w-0 space-y-1 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400 shrink-0" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400 shrink-0" />
            )}
            <Users className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400 shrink-0" />
            <span className="font-medium text-sm truncate">
              Buddy group: {group.group_name}
            </span>
            {group.is_primary && (
              <Badge
                variant="outline"
                className="text-[10px] bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-800"
              >
                Primary
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px]">
              {group.member_count}{' '}
              {group.member_count === 1 ? 'member' : 'members'}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              {totalMemberEvents}{' '}
              {totalMemberEvents === 1 ? 'event entry' : 'event entries'}
            </Badge>
            {!locked && (
              <Badge
                variant="outline"
                className="text-[10px] bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900/40"
              >
                Preferences not set
              </Badge>
            )}
          </div>
          {locked && (first || second) && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3 shrink-0" />
              <span className="truncate">
                <span className="font-medium text-foreground/80">
                  Preferences:
                </span>{' '}
                {first || '—'}
                {second ? ` → ${second}` : ''}
              </span>
            </div>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-violet-300 dark:border-violet-800 bg-background/60">
          {group.members.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground italic">
              No member details available.
            </div>
          ) : (
            <ul className="divide-y divide-violet-200/50 dark:divide-violet-900/40">
              {group.members.map((member) => (
                <li key={member.user_profile_id} className="px-3 py-2">
                  <BuddyGroupMemberRow
                    member={member}
                    buddyGroupPreferencesFirst={group.preferences_first}
                    buddyGroupPreferencesSecond={group.preferences_second}
                    buddyGroupPreferencesLocked={
                      !!group.preferences_locked_at
                    }
                    rodeoTimezone={rodeoTimezone}
                    onNavigateEvent={(eventId) =>
                      onNavigate(`/events/${eventId}`)
                    }
                  />
                </li>
              ))}
            </ul>
          )}
          <div className="px-3 py-2 border-t border-violet-300 dark:border-violet-800 bg-violet-50/30 dark:bg-violet-950/30">
            <button
              type="button"
              className="text-xs font-medium text-violet-700 dark:text-violet-300 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                // Canonical buddy-group management surface for a rodeo
                // is the setup page (BuddyGroupSetupPage at
                // /rodeos/:rodeoId/buddy-group/setup). The bare
                // /buddy-group path doesn't have a route handler — it
                // 404s — so the link was dead.
                onNavigate(`/rodeos/${rodeoId}/buddy-group/setup`);
              }}
            >
              Manage buddy group →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface BuddyGroupMemberRowProps {
  member: UpcomingBuddyGroupMember;
  /** Parent group's preferences — used in the "matches/differs" copy. */
  buddyGroupPreferencesFirst: string | null;
  buddyGroupPreferencesSecond: string | null;
  buddyGroupPreferencesLocked: boolean;
  /** Parent rodeo's IANA timezone, threaded into preference formatters. */
  rodeoTimezone: string;
  onNavigateEvent: (eventId: string) => void;
}

function BuddyGroupMemberRow({
  member,
  buddyGroupPreferencesFirst,
  buddyGroupPreferencesSecond,
  buddyGroupPreferencesLocked,
  rodeoTimezone,
  onNavigateEvent,
}: BuddyGroupMemberRowProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-sm">
          {member.display_name}
          {member.is_self && (
            <span className="text-muted-foreground font-normal text-xs">
              {' '}
              (you)
            </span>
          )}
        </span>
        {member.is_primary && (
          <Badge
            variant="outline"
            className="text-[10px] bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-800"
          >
            Primary
          </Badge>
        )}
        <Badge variant="outline" className="text-[10px]">
          {member.event_count}{' '}
          {member.event_count === 1 ? 'event' : 'events'}
        </Badge>
      </div>
      {member.events.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">
          No events entered yet.
        </div>
      ) : (
        // Each event renders its own card; side entries paint amber
        // so the buddy-vs-side distinction reads at a glance instead
        // of forcing the user to scan badges row by row.
        <ul className="space-y-2">
          {member.events.map((ev) => (
            <li key={ev.registration_id}>
              <BuddyGroupMemberEventEntry
                event={ev}
                buddyGroupPreferencesFirst={buddyGroupPreferencesFirst}
                buddyGroupPreferencesSecond={buddyGroupPreferencesSecond}
                buddyGroupPreferencesLocked={buddyGroupPreferencesLocked}
                rodeoTimezone={rodeoTimezone}
                onNavigateEvent={onNavigateEvent}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface BuddyGroupMemberEventEntryProps {
  event: UpcomingBuddyGroupMember['events'][number];
  buddyGroupPreferencesFirst: string | null;
  buddyGroupPreferencesSecond: string | null;
  buddyGroupPreferencesLocked: boolean;
  /** Parent rodeo's IANA timezone, threaded into preference formatters. */
  rodeoTimezone: string;
  onNavigateEvent: (eventId: string) => void;
}

/**
 * One event entry under a buddy-group member row.
 *
 * Visual model: the entry is wrapped in a card whose background colour
 * carries the "is this tied to the buddy group?" signal, so the user
 * doesn't have to scan a row of badges to figure it out.
 *   - Default neutral card → entry IS tied to this buddy group.
 *   - Amber-tinted card with a "Side entry" header → entry is NOT
 *     tied to this buddy group (typically a team-roping registration
 *     whose partner isn't in the group, so it doesn't ride the
 *     buddy-block draw priority).
 *
 * For side entries we still surface partner name/status, the entry's
 * own draw preferences, and a match/differ note vs the group's locked
 * preferences. For buddy-tied entries we suppress preferences entirely
 * since they inherit the group's locked picks by construction.
 */
function BuddyGroupMemberEventEntry({
  event,
  buddyGroupPreferencesFirst,
  buddyGroupPreferencesSecond,
  buddyGroupPreferencesLocked,
  rodeoTimezone,
  onNavigateEvent,
}: BuddyGroupMemberEventEntryProps) {
  const ownFirst = formatPreferenceLabel(
    event.first_performance_preference,
    rodeoTimezone,
  );
  const ownSecond = formatPreferenceLabel(
    event.second_performance_preference,
    rodeoTimezone,
  );
  const groupFirst = formatPreferenceLabel(
    buddyGroupPreferencesFirst,
    rodeoTimezone,
  );
  const groupSecond = formatPreferenceLabel(
    buddyGroupPreferencesSecond,
    rodeoTimezone,
  );
  const hasOwnPrefs = !!(ownFirst || ownSecond);

  const isSideEntry = !event.via_buddy_group;
  const partnerNotInGroup =
    !!event.partner_user_profile_id && !event.partner_in_buddy_group;

  // The reason this is a side entry. "Partner not in this buddy group"
  // is the dominant case for team roping; we keep the copy generic for
  // solo events so the language still reads correctly when there's no
  // partner involved.
  const sideEntryReason = partnerNotInGroup
    ? 'Partner is not a member of this buddy group'
    : 'Not tied to this buddy group';

  const cardClass = isSideEntry
    ? 'rounded-md border border-amber-300/70 bg-amber-50/70 dark:border-amber-900/40 dark:bg-amber-950/20 px-2.5 py-2 space-y-1'
    : 'rounded-md border bg-background/80 px-2.5 py-2 space-y-1';

  return (
    <div className={cardClass}>
      {isSideEntry && (
        <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-800 dark:text-amber-200">
          <AlertCircle className="h-3 w-3 shrink-0" />
          <span>Side entry — {sideEntryReason}</span>
        </div>
      )}

      <div className="flex items-center gap-1.5 text-xs flex-wrap">
        <button
          type="button"
          className="text-foreground/90 hover:text-primary hover:underline truncate font-medium"
          onClick={(e) => {
            e.stopPropagation();
            onNavigateEvent(event.event_id);
          }}
        >
          {event.event_name}
        </button>
        {event.position && (
          <Badge variant="outline" className="text-[10px] font-normal">
            {event.position}
          </Badge>
        )}
        <StatusBadge
          status={event.status}
          variant="registration"
          className="text-[10px]"
        />
      </div>

      {/* Partner row — shown for any team event with a partner pointer.
          The "not in this buddy group" badge is gone now; the amber
          card already communicates that, and dropping it cuts visual
          noise the user called out. */}
      {(event.partner_name || event.partner_user_profile_id) && (
        <div className="ml-1 flex items-center gap-1.5 text-[11px] text-muted-foreground flex-wrap">
          <span>
            <span className="font-medium text-foreground/70">Partner:</span>{' '}
            {event.partner_name || 'Invited partner'}
          </span>
          {event.partner_status && event.partner_status !== 'none' && (
            <StatusBadge
              status={event.partner_status}
              variant="registration"
              className="text-[10px]"
            />
          )}
        </div>
      )}

      {isSideEntry && hasOwnPrefs && (
        <div className="ml-1 flex items-center gap-1 text-[11px] text-muted-foreground">
          <Clock className="h-3 w-3 shrink-0" />
          <span className="truncate">
            <span className="font-medium text-foreground/70">
              Preferences:
            </span>{' '}
            {ownFirst || '—'}
            {ownSecond ? ` → ${ownSecond}` : ''}
          </span>
        </div>
      )}

      {isSideEntry &&
        buddyGroupPreferencesLocked &&
        hasOwnPrefs &&
        event.preferences_match_buddy_group !== null &&
        (event.preferences_match_buddy_group ? (
          <div className="ml-1 text-[11px] text-emerald-700 dark:text-emerald-300">
            ✓ Preferences match the buddy group
          </div>
        ) : (
          <div className="ml-1 flex items-start gap-1 text-[11px] text-amber-700 dark:text-amber-300">
            <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
            <span>
              Preferences differ from buddy group ({groupFirst || '—'}
              {groupSecond ? ` → ${groupSecond}` : ''}). Side entries can
              deviate — confirm this is intentional.
            </span>
          </div>
        ))}
    </div>
  );
}

interface RegistrationRowProps {
  reg: any;
  rodeoEntriesClosed: boolean;
  /** Parent rodeo's IANA timezone, threaded into preference formatters. */
  rodeoTimezone: string;
  cancellingId: string | null;
  owedByRegistrationId: Map<string, number>;
  onCancelClick: (
    registrationId: string,
    description?: string,
    refundEstimate?: any,
  ) => void;
  onPayNow: (payload: {
    registrationId: string;
    eventName: string;
    fallbackTotal: number;
    transactionFeeTotal: number;
    /** True when the row's current payment is a bank (ACH) debit still
     *  verifying micro-deposits — the parent interposes a supersede warning. */
    achVerificationPending?: boolean;
  }) => void;
  onEditPrefs: (payload: {
    registrationId: string;
    eventId: string;
    rodeoId: string | null;
    eventName: string;
    currentFirst: string | null;
    currentSecond: string | null;
  }) => void;
  onFindPartner: (payload: {
    registrationId: string;
    eventId: string | null;
    primaryPosition: string | null;
    eventName: string;
  }) => void;
  onNavigate: (to: string) => void;
}

function RegistrationRow(props: RegistrationRowProps) {
  const {
    reg,
    rodeoEntriesClosed,
    rodeoTimezone,
    cancellingId,
    owedByRegistrationId,
    onCancelClick,
    onPayNow,
    onEditPrefs,
    onFindPartner,
    onNavigate,
  } = props;
  const eventData = reg.event || {};
  const eventId: string | undefined = eventData.id;
  const eventName = getEventDisplayTitle(eventData);
  const entryFee = eventData.entry_fee;
  const txnFee = eventData.transaction_fee;
  const perSideFee =
    (Number(entryFee) || 0) +
    (Number(txnFee) || 0) +
    (Number(reg.non_member_fee) || 0);
  const partnerPaid = Number((reg as any).partner_payment_amount ?? NaN);
  const primaryPaid = Number((reg as any).payment_amount ?? NaN);
  const totalFee = reg.paid_for_partner
    ? Number.isFinite(primaryPaid) && Number.isFinite(partnerPaid)
      ? primaryPaid + partnerPaid
      : perSideFee * 2
    : perSideFee;
  const regStatus: string = reg.status || 'pending';
  const registrationId: string | undefined = reg.registration_id;

  const regDeadline: string | undefined = eventData.registration_deadline;
  const deadlineWarning =
    !!regDeadline &&
    new Date(regDeadline).getTime() - Date.now() < 48 * 3600000 &&
    new Date(regDeadline).getTime() > Date.now();

  const supplementalOwed = registrationId
    ? owedByRegistrationId.get(registrationId) ?? 0
    : 0;
  // Suppress Pay Now whenever the entry is partnerless on a team event —
  // paying would lock in fees against a row the draw will refuse to include
  // (draw_eligible=false until they invite or accept a new partner). The
  // partner banner directly below already drives them to the Find Partner
  // flow; the Pay button only re-appears once a partner is on the row.
  //
  // Gate on BOTH partner_status AND draw_eligible. partner_status catches
  // the explicit needs_partner flag; draw_eligible catches every other
  // shape the backend uses to mark a row "won't be drawn" (legacy
  // individual rows on team events, mid-cancel survivors, etc.) so a
  // partnerless entry can't slip past just because its partner_status
  // happens to be 'none' / 'cancelled' / 'rejected' instead of
  // 'needs_partner'.
  const needsPartner = (reg as any).partner_status === 'needs_partner';
  const isDrawIneligible = (reg as any).draw_eligible === false;
  // A bank (ACH) debit already CLEARING ('processing') can't be cancelled, so
  // a competing card charge would double-charge if it later succeeds — hide
  // Pay Now and show "Bank payment processing" instead. (A still-verifying ACH
  // stays payable behind the supersede warning, so it's not gated here.)
  const achProcessing = (reg as any).ach_processing === true;
  const achVerifying = (reg as any).ach_verification_pending === true;
  const achVerifyUrl = (reg as any).ach_verification_url as string | undefined;
  // The producer cancelled the whole rodeo: there is nothing to pay for. The
  // payment endpoints refuse these outright, so this is purely about not
  // dangling a button that can only fail.
  const rodeoCancelled = (reg as any).rodeo_cancelled === true;
  const showPayNow =
    !!registrationId &&
    !needsPartner &&
    !isDrawIneligible &&
    !achProcessing &&
    !rodeoCancelled &&
    (regStatus === 'pending_payment' ||
      (regStatus !== 'cancelled' && supplementalOwed > 0));

  return (
    // Left accent stripe (primary) plus a slightly heavier border keeps
    // the My Events card visually distinct from the violet buddy-group
    // card above it — fixes the "they all blend together" feedback.
    <div
      className="relative rounded-lg border-2 border-foreground/10 p-3 pl-4 space-y-2 hover:border-primary/40 transition-colors cursor-pointer overflow-hidden"
      onClick={() => eventId && onNavigate(`/events/${eventId}`)}
    >
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1 bg-primary/70"
      />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-sm truncate">{eventName}</h4>
            <StatusBadge status={regStatus} variant="registration" />
            {/* In-flight bank (ACH) state — verifying micro-deposits, or the
                debit already submitted and clearing. Either way no card charge
                should land on top; the badge explains why. */}
            {achVerifying && regStatus !== 'cancelled' && (
              <Badge
                variant="outline"
                className="text-xs font-normal bg-indigo-50 text-indigo-800 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-900/40"
              >
                Verifying bank
              </Badge>
            )}
            {achProcessing && regStatus !== 'cancelled' && (
              <Badge
                variant="outline"
                className="text-xs font-normal bg-indigo-50 text-indigo-800 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-900/40"
              >
                Bank payment processing
              </Badge>
            )}
            {reg.position && (
              <Badge variant="outline" className="text-xs font-normal">
                {reg.position}
              </Badge>
            )}
            {reg.buddy_group_id && (
              <Badge
                variant="outline"
                className="text-[10px] bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-800"
              >
                Buddy group entry
              </Badge>
            )}
            {reg.payment_status === 'completed' &&
              reg.payment_payer_display_name && (
                <Badge
                  variant="outline"
                  className="text-xs font-normal bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/40"
                >
                  Paid by {reg.payment_payer_display_name}
                </Badge>
              )}
            {reg.payment_status === 'completed' &&
              !reg.payment_payer_display_name &&
              regStatus !== 'cancelled' &&
              (registrationId
                ? (owedByRegistrationId.get(registrationId) ?? 0) <= 0
                : true) && (
                <Badge
                  variant="outline"
                  className="text-xs font-normal bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/40"
                >
                  Paid
                </Badge>
              )}
            {regStatus !== 'cancelled' &&
              registrationId &&
              (owedByRegistrationId.get(registrationId) ?? 0) > 0 && (
                <Badge
                  variant="outline"
                  className="text-xs font-normal bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900/40"
                >
                  Balance Due{' '}
                  {formatCurrency(
                    owedByRegistrationId.get(registrationId) ?? 0,
                  )}
                </Badge>
              )}
          </div>
        </div>
        <div
          className="flex gap-1 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mid-ACH-verification: a "Verify bank" link to Stripe's hosted
              verification page sits alongside Pay Now so the contestant can
              finish the bank check rather than starting a new payment that
              abandons it. */}
          {showPayNow && achVerifying && achVerifyUrl && (
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                window.open(achVerifyUrl, '_blank', 'noopener,noreferrer')
              }
            >
              Verify bank
            </Button>
          )}
          {rodeoCancelled && regStatus !== 'cancelled' && (
            <span className="text-xs font-medium text-destructive whitespace-nowrap">
              Rodeo cancelled — nothing to pay
            </span>
          )}
          {showPayNow &&
            registrationId &&
            (() => {
              const fallbackTotal =
                regStatus === 'pending_payment' ? totalFee : supplementalOwed;
              const txn = Math.max(0, Number(txnFee) || 0);
              return (
                <Button
                  variant={achVerifying ? 'outline' : 'destructive'}
                  size="sm"
                  className={achVerifying ? '' : 'animate-pulse'}
                  onClick={() =>
                    onPayNow({
                      registrationId,
                      eventName,
                      fallbackTotal,
                      transactionFeeTotal:
                        regStatus === 'pending_payment' && reg.paid_for_partner
                          ? txn * 2
                          : 0,
                      achVerificationPending: achVerifying,
                    })
                  }
                >
                  Pay Now
                </Button>
              );
            })()}
          {regStatus !== 'pending_payment' && eventId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onNavigate(`/events/${eventId}`)}
            >
              View
            </Button>
          )}
          {registrationId && !rodeoEntriesClosed && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              disabled={cancellingId === registrationId}
              onClick={() => buildAndOpenCancel(reg, onCancelClick)}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Partner status banners — same logic as before */}
      {reg.partner_status === 'needs_partner' ? (
        <div
          onClick={(e) => e.stopPropagation()}
          className="rounded-md border border-destructive/60 bg-destructive/10 dark:bg-destructive/20 px-3 py-2 text-xs text-destructive dark:text-red-200 space-y-2"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="space-y-0.5">
              <div className="font-semibold">
                No partner — entry will not be drawn
              </div>
              <div className="text-[11px] leading-snug text-destructive/90 dark:text-red-200/90">
                Your previous partner declined. Find a new partner before
                entries close or this entry will be cancelled at the draw.
                Your transaction fee stays in place either way.
              </div>
            </div>
          </div>
          {registrationId && (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  onFindPartner({
                    registrationId,
                    eventId: eventId ?? null,
                    primaryPosition: (reg.position as string) ?? null,
                    eventName,
                  })
                }
              >
                Find Partner
              </Button>
            </div>
          )}
        </div>
      ) : reg.partner_status === 'pending' &&
        reg.registration_type === 'team_primary' ? (
        // Pending-approval block — same partner card layout as the
        // accepted state below, just amber-tinted to signal "waiting".
        // We keep the explanatory copy ("won't be drawn until they
        // accept") since that's a non-obvious consequence the user
        // needs to know about.
        <div
          className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 px-2.5 py-2 text-xs space-y-1"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            <Users className="h-3 w-3 shrink-0 text-amber-700 dark:text-amber-300" />
            <span className="text-amber-800 dark:text-amber-200">Partner:</span>
            <span className="font-medium text-amber-900 dark:text-amber-100">
              {reg.partner_name || 'Invited partner'}
            </span>
            <StatusBadge
              status="pending"
              variant="registration"
              className="ml-1"
            />
          </div>
          <div className="flex items-start gap-1 text-amber-700 dark:text-amber-300">
            <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
            <span>
              Awaiting approval — this entry won't be drawn until they
              accept. We'll notify them again at the registration
              deadline.
            </span>
          </div>
        </div>
      ) : (
        // Partner block — surfaces (a) who the partner is + their
        // invitation status and (b) a payment-state line so the user can
        // tell at a glance whether their partner's seat is covered, who
        // covered it, and for how much. Only renders for team-event rows
        // (team_primary / team_partner) so solo entries stay quiet.
        reg.partner_name && (
          <div
            className="rounded-md border bg-muted/30 px-2.5 py-2 text-xs space-y-1"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1.5 flex-wrap">
              <Users className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground">Partner:</span>
              <span className="font-medium text-foreground">
                {reg.partner_name}
              </span>
              {reg.partner_status && reg.partner_status !== 'none' && (
                <StatusBadge
                  status={reg.partner_status}
                  variant="registration"
                  className="ml-1"
                />
              )}
            </div>
            {(() => {
              const isTeamRow =
                reg.registration_type === 'team_primary' ||
                reg.registration_type === 'team_partner';
              if (!isTeamRow) return null;
              const partnerPaid = Number(reg.partner_payment_amount ?? NaN);
              const primaryPaid = Number(reg.payment_amount ?? NaN);
              // Map a registration row's `payment_method` (the user's
              // CHOICE — pay_later/cash/stripe/paypal) to a
              // human-readable label. Mirrors the labels used on the
              // /profile Payments tab so the dashboard reads the same
              // way as the activity log.
              const labelForChosenMethod = (
                method: string | null | undefined,
              ): string | null => {
                switch (method) {
                  case 'pay_later':
                    return 'Pay later';
                  case 'cash':
                    return 'Cash at event';
                  case 'stripe':
                    return 'Card or Bank';
                  case 'paypal':
                    return 'PayPal';
                  default:
                    return null;
                }
              };
              // Case 1: I'm the responsible payer for both seats
              // (paid_for_partner=true on a team_primary row). Branch
              // again on whether the money has actually been collected:
              // paid_for_partner is just a "responsible payer" flag set
              // at registration time and stays true even when the user
              // chose pay-later, so we can't assume payment is
              // captured. Use the user's own status / payment_status
              // (not the partner's) to decide.
              if (reg.paid_for_partner) {
                const userHasPaid =
                  reg.payment_status === 'completed' ||
                  regStatus === 'paid' ||
                  regStatus === 'confirmed';
                const total =
                  Number.isFinite(primaryPaid) && Number.isFinite(partnerPaid)
                    ? primaryPaid + partnerPaid
                    : Number.isFinite(primaryPaid)
                      ? primaryPaid
                      : null;
                if (userHasPaid) {
                  return (
                    <div className="flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                      <span aria-hidden>✓</span>
                      <span>
                        You paid for both
                        {total !== null ? ` — ${formatCurrency(total)}` : ''}
                      </span>
                    </div>
                  );
                }
                // User hasn't settled yet but is on the hook for both
                // seats. Skip the dollar amount (the Balance Due badge
                // in the row header already states it) — instead show
                // the user's chosen payment method so they remember
                // they picked Pay Later vs Cash at Event vs an online
                // method that's still pending capture.
                const chosen = labelForChosenMethod(
                  reg.selected_payment_method as string | null,
                );
                return (
                  <div className="flex items-center gap-1 text-amber-700 dark:text-amber-300">
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    <span>
                      You're covering both seats —{' '}
                      {chosen ?? 'payment pending'}
                    </span>
                  </div>
                );
              }
              // Case 2: Partner paid their share online (payment row
              // captured + completed).
              if (reg.partner_payment_status === 'completed') {
                return (
                  <div className="flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                    <span aria-hidden>✓</span>
                    <span>
                      Partner paid
                      {Number.isFinite(partnerPaid) && partnerPaid > 0
                        ? ` — ${formatCurrency(partnerPaid)}`
                        : ''}
                    </span>
                  </div>
                );
              }
              // Case 3: Partner registration marked 'paid' without a
              // captured online payment — secretary recorded cash/manual
              // entry. Don't surface a dollar amount since we don't have
              // a payment row to source it from reliably.
              if (
                reg.partner_registration_status === 'paid' ||
                reg.partner_registration_status === 'confirmed'
              ) {
                return (
                  <div className="flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                    <span aria-hidden>✓</span>
                    <span>Partner paid</span>
                  </div>
                );
              }
              // Case 4: Partner still owes their share. Only fire when
              // we have an explicit "unpaid" signal (status pending_*
              // or a pending payment row) so we don't accidentally
              // accuse a partner whose state we just don't have data
              // for.
              // A cancelled rodeo owes nothing, so the partner isn't behind
              // on anything — dunning them for a dead event is just noise.
              const isPartnerUnpaid =
                !rodeoCancelled &&
                (reg.partner_registration_status === 'pending_payment' ||
                  reg.partner_registration_status ===
                    'pending_confirmation_and_payment' ||
                  reg.partner_payment_status === 'pending');
              if (isPartnerUnpaid) {
                const partnerChosen = labelForChosenMethod(
                  reg.partner_selected_payment_method as string | null,
                );
                const owedSuffix =
                  Number.isFinite(partnerPaid) && partnerPaid > 0
                    ? ` — owes ${formatCurrency(partnerPaid)}`
                    : '';
                const methodSuffix = partnerChosen
                  ? ` (${partnerChosen})`
                  : '';
                return (
                  <div className="flex items-center gap-1 text-amber-700 dark:text-amber-300">
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    <span>
                      Partner hasn't paid yet{owedSuffix}
                      {methodSuffix}
                    </span>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )
      )}

      {/* Performance preferences + Edit affordance (hidden on buddy-group
          rows since those are locked at the group level, and on finals
          rodeos where every contestant runs every performance). */}
      {reg.rodeo?.is_finals !== true && (() => {
        const canEdit =
          !!registrationId && !reg.buddy_group_id && !rodeoEntriesClosed;
        return (
          <div className="flex items-start justify-between gap-2">
            <EntryPreferencesDisplay
              entry={reg as EntryWithPreferences}
              timezone={rodeoTimezone}
            />
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditPrefs({
                    registrationId: registrationId as string,
                    eventId: eventId as string,
                    rodeoId: (reg.rodeo?.id as string | undefined) ?? null,
                    eventName,
                    currentFirst:
                      (reg.first_performance_preference as string | null) ??
                      null,
                    currentSecond:
                      (reg.second_performance_preference as string | null) ??
                      null,
                  });
                }}
              >
                Edit
              </Button>
            )}
          </div>
        );
      })()}

      {deadlineWarning && (
        <div className="flex items-center gap-1 text-xs text-amber-600">
          <AlertCircle className="h-3 w-3" />
          Registration closes {getRelativeTime(regDeadline)}
        </div>
      )}
    </div>
  );
}

/**
 * Compute the cancel-dialog payload (description + itemized refund
 * estimate) for a registration row, then forward to the cancel handler.
 * Mirrors the per-side / full-team math the event-detail page renders so
 * dashboard cancellations get the same disclosure copy.
 */
function buildAndOpenCancel(
  reg: any,
  onCancelClick: (
    registrationId: string,
    description?: string,
    refundEstimate?: any,
  ) => void,
) {
  const registrationId = reg.registration_id;
  if (!registrationId) return;
  const eventData = reg.event || {};
  const eventName = getEventDisplayTitle(eventData);
  const entryFee = eventData.entry_fee;
  const txnFee = eventData.transaction_fee;
  const perSideFee =
    (Number(entryFee) || 0) +
    (Number(txnFee) || 0) +
    (Number(reg.non_member_fee) || 0);

  const paid = Number(reg.payment_amount);
  const provider = reg.payment_method;
  const isPaidOnline =
    (provider === 'stripe' || provider === 'paypal') &&
    (reg.payment_status === 'completed' || reg.payment_status === 'paid');
  let refundEstimate: any | undefined;
  if (isPaidOnline && Number.isFinite(paid) && paid > 0) {
    const perRegEntry = Math.max(0, Number(entryFee) || 0);
    const perRegTxn = Math.max(0, Number(txnFee) || 0);
    const perRegNonMember = Math.max(0, Number(reg.non_member_fee) || 0);
    const isFullTeamCancel =
      !!reg.paid_for_partner && reg.registration_type === 'team_primary';
    const partnerPaid = Number((reg as any).partner_payment_amount ?? NaN);
    const partnerNonMember = Number(
      (reg as any).partner_non_member_fee ?? NaN,
    );
    const partnerSideNonMember = Number.isFinite(partnerNonMember)
      ? partnerNonMember
      : perRegNonMember;
    const primaryPaid = Number((reg as any).payment_amount ?? NaN);
    const totalPaid = isFullTeamCancel
      ? Number.isFinite(primaryPaid) && Number.isFinite(partnerPaid)
        ? primaryPaid + partnerPaid
        : perSideFee * 2
      : paid;
    const transactionFee = perRegTxn * (isFullTeamCancel ? 2 : 1);
    const baseFees =
      perRegEntry * (isFullTeamCancel ? 2 : 1) +
      (isFullTeamCancel
        ? perRegNonMember + partnerSideNonMember
        : perRegNonMember) +
      transactionFee;
    const swapFeeRetained = isFullTeamCancel
      ? Math.max(0, Math.round((totalPaid - baseFees) * 100) / 100)
      : 0;
    const refundable = Math.max(
      0,
      totalPaid - transactionFee - swapFeeRetained,
    );
    refundEstimate = {
      paidTotal: totalPaid,
      entryFee: perRegEntry * (isFullTeamCancel ? 2 : 1),
      nonMemberFee: isFullTeamCancel
        ? perRegNonMember + partnerSideNonMember
        : perRegNonMember,
      transactionFee,
      partnerSwapFeeRetained: swapFeeRetained,
      refundable,
      currency: 'USD' as const,
    };
  }
  const description = refundEstimate
    ? undefined
    : `Cancel your ${reg.position ?? ''} registration for ${eventName}?`.trim();
  onCancelClick(registrationId, description, refundEstimate);
}
